import { nextTick } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useRouter } from "vue-router";
import type { LoginStartRequest, PortalSettings } from "@ai-platform/contracts";
import { callRoute } from "../api/client.ts";
import { homeFor } from "../session/access.ts";
import { forgetSessionAfterLogout, SESSION_KEY } from "../session/session.ts";
import { DEFAULT_SETTINGS, SETTINGS_KEY } from "../session/settings.ts";

/** Leaves the page for the authorize address; a real navigation, the state of the page is gone (05 §6.1). */
export type Navigate = (url: string) => void;

const assignLocation: Navigate = (url) => window.location.assign(url);

/**
 * `POST /api/auth/login/start` with the e-mail address, the instance address or the staff entry
 * (07 §2.3, ADR-065, ADR-078). When the API started the flow, off to the address it returns;
 * when it names a next step (`next: "instance"`), the mutation's `data` carries it and the
 * dialog shows that step. The return path travels in the request and stays on the server,
 * because the page is gone after the redirect.
 */
export function useLoginStart(navigate: Navigate = assignLocation) {
  return useMutation({
    // the route's name as key, for the query client's report of a failure (PF4)
    mutationKey: ["loginStart"],
    mutationFn: (body: LoginStartRequest) => callRoute("loginStart", { body }),
    onSuccess: (data) => {
      if ("authorizeUrl" in data) navigate(data.authorizeUrl);
    },
  });
}

/**
 * `POST /api/auth/logout`, then the page a guest sees opens, the catalogue or, with the guest
 * view off, the login (`homeFor`, ADR-133 point 3), and every server datum is forgotten (ADR-076
 * point 5, ADR-083 point 5). Order matters: the session becomes `null` first, so the guard of the
 * navigation sees a guest and not a session that ended; then the pages of the signed-in user
 * unmount, and what the guest's page still watches is fetched again as the guest.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const router = useRouter();
  return useMutation({
    mutationKey: ["logout"],
    mutationFn: () => callRoute("logout"),
    onSuccess: async () => {
      queryClient.setQueryData(SESSION_KEY, null);
      const settings = queryClient.getQueryData<PortalSettings>(SETTINGS_KEY) ?? DEFAULT_SETTINGS;
      await router.push({ name: homeFor(null, settings) });
      // the page of the signed-in user unmounts with the next render, its queries go inactive
      await nextTick();
      await forgetSessionAfterLogout(queryClient);
    },
  });
}
